import { useState, useEffect } from 'react'
import liff from '@line/liff'

interface LiffState {
  isReady: boolean
  isLoggedIn: boolean
  profile: {
    userId: string
    displayName: string
    pictureUrl?: string
  } | null
  accessToken: string | null
  error: string | null
}

export const useLiff = () => {
  const [state, setState] = useState<LiffState>({
    isReady: false,
    isLoggedIn: false,
    profile: null,
    accessToken: null,
    error: null
  })

  useEffect(() => {
    const initLiff = async () => {
      try {
        const liffId = import.meta.env.VITE_LIFF_ID
        if (!liffId) {
          throw new Error('LIFF IDが設定されていません')
        }

        await liff.init({ liffId })
        
        const isLoggedIn = liff.isLoggedIn()
        
        if (!isLoggedIn) {
          // 自動ログイン
          liff.login()
          return
        }

        // プロフィール取得
        const profile = await liff.getProfile()
        const accessToken = liff.getAccessToken()

        setState({
          isReady: true,
          isLoggedIn: true,
          profile: {
            userId: profile.userId,
            displayName: profile.displayName,
            pictureUrl: profile.pictureUrl
          },
          accessToken,
          error: null
        })
      } catch (error) {
        console.error('LIFF initialization failed:', error)
        setState(prev => ({
          ...prev,
          isReady: true,
          error: error instanceof Error ? error.message : 'LIFF初期化に失敗しました'
        }))
      }
    }

    initLiff()
  }, [])

  const login = () => {
    liff.login()
  }

  const logout = () => {
    liff.logout()
    setState({
      isReady: true,
      isLoggedIn: false,
      profile: null,
      accessToken: null,
      error: null
    })
  }

  const closeWindow = () => {
    if (liff.isInClient()) {
      liff.closeWindow()
    }
  }

  const sendMessage = (message: string) => {
    if (liff.isInClient()) {
      liff.sendMessages([{
        type: 'text',
        text: message
      }])
    }
  }

  return {
    ...state,
    login,
    logout,
    closeWindow,
    sendMessage,
    isInClient: liff.isInClient()
  }
}